// Limits of what a recording can claim, computed from its watts (ADR 0022). The client sends its samples; these rules
// decide what the server keeps of them. They bound what a modified client can claim without faking its power too, and
// stay generous on purpose: an honest ride, in a peloton or with items, must never be touched.
import { airFactor, cdaFor, BIKE_KG } from './physics.js';
import type { Samples } from './stats.js';

/** Most power and least drag a race can give at once: a turbo, full shelter, and the thin air of the highest track. */
const MAX_POWER_BOOST = 1.5, MIN_DRAG = 0.45, MIN_AIR = airFactor(4800);
const AIR_DENSITY = 1.2, G = 9.81;
/** What a ride may hold before its first watts: a descent to the line, the speed of a rolling start. */
export const DISTANCE_SLACK_M = 2000, ELEVATION_SLACK_M = 100;

/** Top speed (m/s) these watts sustain on the flat in the best conditions, rolling resistance ignored. */
const bestSpeed = (watts: number, mass: number) => Math.cbrt((2 * Math.max(0, watts) * MAX_POWER_BOOST) / (AIR_DENSITY * cdaFor(mass) * MIN_DRAG * MIN_AIR));

/** The most distance (m) the recorded watts can cover, second by second (without the slack). */
export function reachableDistance(p: number[], mass: number): number[] {
  let s = 0;
  return p.map(w => (s += bestSpeed(w, mass)));
}

/** The most climbing (m) the recorded work can lift a rider and their bike. */
export function reachableElevation(p: number[], mass: number): number {
  return (p.reduce((a, w) => a + Math.max(0, w), 0) * MAX_POWER_BOOST) / ((mass + BIKE_KG) * G) + ELEVATION_SLACK_M;
}

/** Elevation gained along the altitude channel. */
const gainOf = (alt: number[]) => { let g = 0; for (let i = 1; i < alt.length; i++) g += Math.max(0, alt[i] - alt[i - 1]); return g; };

/**
 * The samples with their distance and altitude brought within what the watts allow. Scaled rather than refused: the
 * ride still counts for what it can be. Returns the same object when nothing was out of reach, as for any honest ride.
 */
export function boundRide(S: Samples, mass: number): { samples: Samples; capped: boolean } {
  const n = S.d.length;
  if (!n) return { samples: S, capped: false };
  const reach = reachableDistance(S.p, mass)[n - 1] + DISTANCE_SLACK_M, gain = gainOf(S.alt), lift = reachableElevation(S.p, mass);
  const dk = S.d[n - 1] > reach ? reach / S.d[n - 1] : 1, ak = gain > lift ? lift / gain : 1;
  if (dk === 1 && ak === 1) return { samples: S, capped: false };
  return {
    capped: true,
    samples: { ...S, d: dk === 1 ? S.d : S.d.map(d => +(d * dk).toFixed(1)), alt: ak === 1 ? S.alt : S.alt.map(a => +(S.alt[0] + (a - S.alt[0]) * ak).toFixed(1)) },
  };
}

/** The least time (s) in which the recorded watts can cover `goal` metres; null when they never do. */
export function fastestTime(p: number[], mass: number, goal: number): number | null {
  const at = reachableDistance(p, mass).findIndex(s => s + DISTANCE_SLACK_M >= goal);
  return at < 0 ? null : at + 1;
}

/**
 * Best power (W/kg) over a duration (s) no human has held: above the records of the world's best, with a margin, so a
 * strong rider is never flagged and a calibration error rarely is. A flag only keeps the ride out of the rankings.
 */
export const POWER_LIMITS: Record<number, number> = { 5: 32, 60: 16, 300: 10, 1200: 8, 3600: 7.2 };

/** Whether the best efforts of a ride go past POWER_LIMITS. */
export function impossiblePower(bests: Record<number, number>, mass: number): boolean {
  return Object.entries(POWER_LIMITS).some(([d, wkg]) => (bests[+d] || 0) / Math.max(1, mass) > wkg);
}

/** Hits a rider can land in a ride: one every few seconds at most. */
export const maxHits = (seconds: number) => Math.floor(seconds / 3);
/** Below this, a solo ride's finishing place earns nothing: a race takes longer than this to ride. */
export const MIN_PLACED_S = 60;
